import { Component, effect, EventEmitter, Input, NO_ERRORS_SCHEMA, OnChanges, OnInit, Output, signal, ViewChild } from "@angular/core";
import { Utils } from '../../../../shared/utils';
import { IMPORTS } from './_imports';
import { ToastrService } from "ngx-toastr";
import { FlowObjectDefinitionService } from "src/app/services/flow-object-definition.service";
import { FlowDefinition } from "src/app/models/flow.model";
import { ConfigSchema, ConfigSchemaSectionAccessLevel, ConfigSchemaSectionDocumentClass } from "src/app/models/config-schema.model";
import { Enums } from "src/app/shared/enums";
import { IBaseOption } from "src/app/models/base.model";
import { AccessLevelComponent } from "../_edocs-fields/access-level/access-level.component";
import { FlowObjectDefinition, FlowObjectType, FormSchema } from "src/app/models/flow-object.model";
import { FOD_ADD_TO_PROCESS_TINYMCE_OPTIONS } from './tinymce-options';

@Component({
    selector: 'fod-add-to-process',
    templateUrl: './fod-add-to-process.component.html',
    styleUrl: './fod-add-to-process.component.scss',
    standalone: true,
    imports: IMPORTS,
    schemas: [NO_ERRORS_SCHEMA]
})
export class FlowObjectDetailsAddToProcessComponent implements OnInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('accessLevelRef') accessLevelRef: AccessLevelComponent;
    // #endregion

    // #region [Type props]
    FlowObjectType = FlowObjectType;
    ProcessOrigin = Enums.ProcessOrigin;
    // #endregion

    // #region [const]
    readonly PROCESS_MASK_REGEX = /^\d{4}-[A-Za-z0-9]{5}$/;
    // #endregion

    // #region [props]
    formFlowObjectSchemaFields: any[] = [];
    tinyMceOptionsProcess: any = FOD_ADD_TO_PROCESS_TINYMCE_OPTIONS;
    // #endregion

    // #region [signals]
    $configSchema = signal(null as ConfigSchema);
    $flowDefinition = signal(null as FlowDefinition);
    $isReadOnlyMode = signal(false);
    $processOrigin = signal(Enums.ProcessOrigin.Instance);
    $configSchemaSectionAccessLevel = signal(new ConfigSchemaSectionAccessLevel());
    $processProtocolFormFieldOptions = signal([] as IBaseOption[]);
    $processProtocolFormField = signal(null as string);
    $addToProcessSummary = signal('');
    $isAddToProcessSummaryBootstrapFinished = signal(false);
    $cancellationDocumentClass = signal(null as ConfigSchemaSectionDocumentClass);
    $processProtocol = signal('');
    $isProcessProtocolValid = signal(true);
    // #endregion

    // #region [effects]
    $isProcessProtocolValidTriggerEvent = effect(() => {
        this._isProcessProtocolValidEvent.emit(this.$isProcessProtocolValid());
    }, { allowSignalWrites: true });
    // #endregion

    // #region [Input/Output]
    @Input() _configSchema: ConfigSchema;
    @Input() _flowDefinition: FlowDefinition;
    @Input() _isReadOnlyMode: boolean;
    @Output() _configSchemaChangeEvent = new EventEmitter<ConfigSchema>();
    @Output() _processOriginChangeEvent = new EventEmitter<Enums.ProcessOrigin>();
    @Output() _isProcessProtocolValidEvent = new EventEmitter<boolean>();
    // #endregion

    constructor(
        private toastr: ToastrService,
        private flowObjectDefinitionService: FlowObjectDefinitionService
    ) { }

    async ngOnInit() {
        let formFlowObject = this.$flowDefinition().flowObjectDefinitions.find(x => x.typeId == FlowObjectType.StartForm);
        if (formFlowObject != null) {
            if (Utils.isNullOrEmpty(formFlowObject.formSchema)) {
                const response = await this.flowObjectDefinitionService.getFormSchema(formFlowObject.id);

                if (!response.isSuccess) {
                    this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                formFlowObject.formSchema = response.data;
            }

            let formSchema = JSON.parse(formFlowObject.formSchema) as FormSchema;
            this.formFlowObjectSchemaFields = Utils.getFormFieldsWithUsableValue(formSchema.components);
        }

        for (let prop in this.$configSchemaSectionAccessLevel()) {
            Utils.updSignal(this.$configSchemaSectionAccessLevel, x => {
                x[prop] = this.$configSchema().taskAddToProcess.accessLevel[prop] || x[prop];
                return x;
            });
        }

        this.initAddToProcessInfo();

        this.tinyMceOptionsProcess.addDynamicMenu = editor => {
            editor.ui.registry.addMenuButton('vForm', {
                text: 'Formulário',
                fetch: callback => callback(this.getTinyMceMenuFormEntries(formFlowObject, editor))
            });
        };

        // debounce para que a opção de menu "Formulário" possa carregar corretamente
        setTimeout(() => this.$isAddToProcessSummaryBootstrapFinished.set(true), 100);
    }

    ngOnChanges() {
        this.$configSchema.set(this._configSchema);
        this.$flowDefinition.set(this._flowDefinition);
        this.$isReadOnlyMode.set(this._isReadOnlyMode);
    }

    processOriginChange(event?) {
        if (event != null) {
            this.$processOrigin.set(event);
        }

        if (this.$processOrigin() !== Enums.ProcessOrigin.Form) {
            this.$processProtocolFormField.set(null);
        }

        if (this.$processOrigin() !== Enums.ProcessOrigin.PublicAgentDeliberation) {
            this.$processProtocol.set('');
            this.$isProcessProtocolValid.set(true);
        }

        Utils.updSignal(this.$configSchema, x => {
            x.taskAddToProcess.processOrigin = this.$processOrigin();
            x.taskAddToProcess.processProtocolFormField = this.$processProtocolFormField();
            x.taskAddToProcess.processProtocol = this.$processProtocol();
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());
        this._processOriginChangeEvent.emit(this.$processOrigin());
    }

    onProcessProtocolFormFieldChange(value: string) {
        this.$processProtocolFormField.set(value);

        Utils.updSignal(this.$configSchema, x => {
            x.taskAddToProcess.processProtocolFormField = this.$processProtocolFormField();
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    onProcessProtocolChange(value: string) {
        this.$processProtocol.set(value);

        const isValid = Utils.isNullOrEmpty(value) || this.PROCESS_MASK_REGEX.test(value);
        this.$isProcessProtocolValid.set(isValid);

        Utils.updSignal(this.$configSchema, x => {
            x.taskAddToProcess.processProtocol = value;
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    updateSourceCode(forcedConfigSchema: ConfigSchema) {
        Utils.setSignal(this.$configSchema, forcedConfigSchema);

        Utils.setSignal(this.$configSchemaSectionAccessLevel, Object.assign(this.$configSchemaSectionAccessLevel(), this.$configSchema().taskAddToProcess.accessLevel));

        this.initAddToProcessInfo();
        this.accessLevelRef?.initLegalReasoningsInfo();

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    resolveAddToProcessInfo(): boolean {
        if (this.$addToProcessSummary()?.length < 5) {
            this.toastr.error(Enums.Messages.MandatoryAddToProcessSummary, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return false;
        }

        let div = document.createElement('div');
        div.innerHTML = this.$addToProcessSummary()?.replaceAll('<br />', '\n');
        Utils.updSignal(this.$configSchema, x => {
            x.taskAddToProcess.addToProcessSummary = div.textContent || div.innerText || '';
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());

        return true;
    }

    onUpdateAccessLevel(event: ConfigSchemaSectionAccessLevel) {
        Utils.setSignal(this.$configSchemaSectionAccessLevel, Object.assign(this.$configSchemaSectionAccessLevel(), event));
        Utils.updSignal(this.$configSchema, x => {
            Object.assign(x.taskAddToProcess.accessLevel, this.$configSchemaSectionAccessLevel());
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    onUpdateCancellationDocumentClass(event: ConfigSchemaSectionDocumentClass) {
        Utils.setSignal(this.$cancellationDocumentClass, event);

        Utils.updSignal(this.$configSchema, x => {
            x.taskAddToProcess.cancellationDocumentClass = event;
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    /* ====================== */
    // private methods
    /* ====================== */

    private initAddToProcessInfo() {
        const taskAddToProcess = this.$configSchema()?.taskAddToProcess;
        if (!taskAddToProcess) return;

        const savedProcessOrigin = taskAddToProcess.processOrigin ?? Enums.ProcessOrigin.Instance;
        this.$processOrigin.set(savedProcessOrigin);

        this.$addToProcessSummary.set(taskAddToProcess.addToProcessSummary?.replaceAll('\n', '<br />'));
        this.$processProtocolFormFieldOptions.set(this.formFlowObjectSchemaFields.map(x => ({ value: x.key, description: x.label })));
        this.$processProtocol.set(taskAddToProcess.processProtocol || '');
        this.$processProtocolFormField.set(taskAddToProcess.processProtocolFormField);

        Utils.setSignal(this.$cancellationDocumentClass, taskAddToProcess.cancellationDocumentClass || new ConfigSchemaSectionDocumentClass());
    }

    private getTinyMceMenuFormEntries(flowObject: FlowObjectDefinition, editor): any[] {
        if (Utils.isNullOrEmpty(flowObject.formSchema)) return;

        let entries = [];
        let formSchema = JSON.parse(flowObject.formSchema) as FormSchema;

        Utils.getFormFieldsWithUsableValue(formSchema.components).forEach(item => {
            entries.push({
                type: 'menuitem',
                text: item.label,
                onAction: () => editor.insertContent(`{|${item.key}|}`)
            });
        });

        return entries;
    }
}
